import { useMemo, useState } from 'react';
import { MilestoneGantt } from '@/components/ui/charts';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import { WorkspaceButton } from '@/components/ui/workspace/button';
import { WorkspaceField, WorkspaceInput } from '@/components/ui/workspace/field';
import { useCanAdjustAssessment } from '@/lib/api/health-readiness';
import {
  isAiDerivedMilestone,
  useCreateMilestoneMutation,
  useScheduleMilestonesQuery,
  useUpdateMilestoneMutation,
  type ScheduleMilestone,
} from '@/lib/api/health-schedule';
import {
  MIN_REASON_CHARS,
  changesADate,
  milestoneChanges,
  milestoneDraftOf,
  type MilestoneDraft,
} from './detail-model';
import { DetailCard, Meta, OFORM, P_LABEL, TEXTAREA } from './detail-parts';
import { localDayIso, toGanttMilestone } from './overview/overview-model';

/**
 * The Schedule domain's milestones (T-0243 / E22-F02; prototype
 * `scheduleBlock()`): the milestones on a month axis, planned and
 * projected with today marked (`MilestoneGantt`), then the add row. A
 * milestone's name opens its edit form below the chart: forecast, actual
 * and the IV&V gate for anyone on the engagement, the planned date for a
 * lead. A date change on a milestone the AI proposed needs a reason, which
 * the date history keeps (the API's override). The slip and the status
 * come from the API.
 */

/** The override tag a milestone date change carries (the taxonomy's general reason). */
const DATE_CHANGE_TAG = 'other';

export function ScheduleBlock({ engagementId }: { engagementId: string }) {
  const { milestones, isLoading, error } = useScheduleMilestonesQuery(engagementId);
  const today = useMemo(() => localDayIso(new Date()), []);
  const [editing, setEditing] = useState<string | null>(null);
  const editingMilestone = milestones.find((m) => m.registerEntryId === editing) ?? null;

  return (
    <DetailCard title="Milestones" aside={<Meta>planned and projected · today marked</Meta>}>
      {isLoading ? (
        <div className="flex items-center gap-2 text-meta text-ink-500">
          <Spinner /> Loading milestones…
        </div>
      ) : error ? (
        <ErrorMessage error={error} />
      ) : (
        <MilestoneGantt
          milestones={milestones.map(toGanttMilestone)}
          today={today}
          renderName={(m) => (
            <button
              type="button"
              aria-label={`Edit ${m.name}`}
              aria-expanded={editing === m.id}
              onClick={() => setEditing(editing === m.id ? null : m.id)}
              className="truncate text-left hover:underline hover:underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
            >
              {m.name}
            </button>
          )}
        />
      )}
      {editingMilestone ? (
        <MilestoneEditForm
          key={editingMilestone.registerEntryId}
          engagementId={engagementId}
          milestone={editingMilestone}
          onDone={() => setEditing(null)}
        />
      ) : null}
      <AddMilestoneRow engagementId={engagementId} />
    </DetailCard>
  );
}

function AddMilestoneRow({ engagementId }: { engagementId: string }) {
  const create = useCreateMilestoneMutation(engagementId);
  const [name, setName] = useState('');
  const [planned, setPlanned] = useState('');
  const [forecast, setForecast] = useState('');
  const canSubmit = name.trim().length > 0 && planned.length > 0 && !create.isPending;
  return (
    <form
      aria-label="Add milestone"
      className="mt-3.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        create.mutate(
          { name: name.trim(), planned_date: planned, forecast_date: forecast || null },
          {
            onSuccess: () => {
              setName('');
              setPlanned('');
              setForecast('');
            },
          },
        );
      }}
    >
      <div className="grid items-end gap-2 sm:grid-cols-[2fr_1fr_1fr_auto]">
        <WorkspaceField label="Name">
          {(id) => (
            <WorkspaceInput
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. UAT exit gate"
            />
          )}
        </WorkspaceField>
        <WorkspaceField label="Planned date">
          {(id) => (
            <WorkspaceInput
              id={id}
              type="date"
              value={planned}
              onChange={(e) => setPlanned(e.target.value)}
            />
          )}
        </WorkspaceField>
        <WorkspaceField label="Forecast (optional)">
          {(id) => (
            <WorkspaceInput
              id={id}
              type="date"
              value={forecast}
              onChange={(e) => setForecast(e.target.value)}
            />
          )}
        </WorkspaceField>
        <WorkspaceButton type="submit" size="md" disabled={!canSubmit}>
          {create.isPending ? 'Adding…' : 'Add milestone'}
        </WorkspaceButton>
      </div>
      {create.error ? <ErrorMessage error={create.error} className="mt-2" /> : null}
    </form>
  );
}

function MilestoneEditForm({
  engagementId,
  milestone,
  onDone,
}: {
  engagementId: string;
  milestone: ScheduleMilestone;
  onDone: () => void;
}) {
  const canEditPlanned = useCanAdjustAssessment();
  const update = useUpdateMilestoneMutation(engagementId);
  const [draft, setDraft] = useState<MilestoneDraft>(() => milestoneDraftOf(milestone));
  const [reason, setReason] = useState('');
  const aiDerived = isAiDerivedMilestone(milestone.origin);
  const body = milestoneChanges(milestone, draft, { canEditPlanned });
  const changed = Object.keys(body).length > 0;
  const needsReason = aiDerived && changesADate(body);
  const reasonOk = !needsReason || reason.trim().length >= MIN_REASON_CHARS;
  const set = (patch: Partial<MilestoneDraft>) => setDraft((d) => ({ ...d, ...patch }));

  return (
    <form
      aria-label={`Edit ${milestone.name}`}
      className={`${OFORM} mt-3`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!changed || !reasonOk) return;
        update.mutate(
          {
            milestoneId: milestone.registerEntryId,
            body: needsReason
              ? { ...body, override: { tag: DATE_CHANGE_TAG, rationale: reason.trim() } }
              : body,
          },
          { onSuccess: onDone },
        );
      }}
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <span className={P_LABEL}>Edit {milestone.name}</span>
        {aiDerived ? (
          <StatusChip tone="ai" size="sm">
            from the AI
          </StatusChip>
        ) : null}
      </div>
      <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <WorkspaceField label="Planned" help={canEditPlanned ? undefined : 'A lead moves the plan'}>
          {(id, describedBy) => (
            <WorkspaceInput
              id={id}
              type="date"
              value={draft.planned}
              disabled={!canEditPlanned}
              aria-describedby={describedBy}
              onChange={(e) => set({ planned: e.target.value })}
            />
          )}
        </WorkspaceField>
        <WorkspaceField label="Forecast">
          {(id) => (
            <WorkspaceInput
              id={id}
              type="date"
              value={draft.forecast}
              onChange={(e) => set({ forecast: e.target.value })}
            />
          )}
        </WorkspaceField>
        <WorkspaceField label="Actual">
          {(id) => (
            <WorkspaceInput
              id={id}
              type="date"
              value={draft.actual}
              onChange={(e) => set({ actual: e.target.value })}
            />
          )}
        </WorkspaceField>
        <label className="flex h-input-h items-center gap-2 text-body-sm text-ink-700">
          <input
            type="checkbox"
            checked={draft.gate}
            onChange={(e) => set({ gate: e.target.checked })}
            className="h-4 w-4 accent-accent-amber-600"
          />
          IV&amp;V gate
        </label>
      </div>
      {needsReason ? (
        <div>
          <textarea
            aria-label="Reason for the date change"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why the date moves; the date history keeps it"
            rows={2}
            className={TEXTAREA}
          />
          <p className="mt-1 text-meta text-ink-500">
            {`The AI proposed this milestone, so a date change needs a reason (${MIN_REASON_CHARS} characters or more).`}
          </p>
        </div>
      ) : null}
      {update.error ? <ErrorMessage error={update.error} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        <WorkspaceButton
          type="submit"
          variant="primary"
          disabled={!changed || !reasonOk || update.isPending}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </WorkspaceButton>
        <WorkspaceButton variant="tertiary" onClick={onDone} disabled={update.isPending}>
          Cancel
        </WorkspaceButton>
        <span className="text-meta text-ink-500">
          An actual date marks it done; the slip is worked out by the API.
        </span>
      </div>
    </form>
  );
}
